import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import './Dashboard.css';

const LINE_TEXTAREA_STYLE = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 14,
  lineHeight: 1.5,
};

const MIN_TEXTAREA_HEIGHT_PX = 120;

const textInputStyle = {
  ...LINE_TEXTAREA_STYLE,
  width: '100%',
  boxSizing: 'border-box',
};

function normalizeLevel(raw) {
  const n = parseInt(raw, 10);
  return Number.isNaN(n) || n < 1 ? 1 : n;
}

function WritingSkill() {
  const [types, setTypes] = useState([]);
  const [skills, setSkills] = useState([]);
  const [keywordPool, setKeywordPool] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillLoading, setSkillLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [typeId, setTypeId] = useState('');
  /** '' | 'new' | string skill_id */
  const [skillSel, setSkillSel] = useState('');
  const [form, setForm] = useState({
    name: '',
    definition: '',
    advantage: '',
    level: 1,
  });
  const [tickedKeywords, setTickedKeywords] = useState(() => new Set());
  const [newKeyword, setNewKeyword] = useState('');
  const [examples, setExamples] = useState([]);
  const [deletingExampleId, setDeletingExampleId] = useState(null);

  const loadKeywordPool = useCallback(async () => {
    const res = await api.get('/data/writing-skill/keywords');
    const list = res.data?.keywords || [];
    setKeywordPool([...list].sort((a, b) => String(a).localeCompare(String(b))));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setTypesLoading(true);
      try {
        const res = await api.get('/data/writing-skill/types');
        if (!cancelled) setTypes(res.data?.types || []);
      } catch (err) {
        if (!cancelled) {
          setMessage({
            type: 'error',
            text: err.response?.data?.error || err.response?.data?.details || 'Failed to load skill types',
          });
          setTypes([]);
        }
      } finally {
        if (!cancelled) setTypesLoading(false);
      }
    };
    load();
    loadKeywordPool().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loadKeywordPool]);

  useEffect(() => {
    if (!typeId) {
      setSkills([]);
      setSkillSel('');
      setForm({ name: '', definition: '', advantage: '', level: 1 });
      setTickedKeywords(new Set());
      setExamples([]);
      return;
    }
    let cancelled = false;
    const loadSkills = async () => {
      setSkillsLoading(true);
      setSkillSel('');
      setForm({ name: '', definition: '', advantage: '', level: 1 });
      setTickedKeywords(new Set());
      setExamples([]);
      try {
        const res = await api.get('/data/writing-skill/skills', { params: { typeId } });
        if (!cancelled) setSkills(res.data?.skills || []);
      } catch (err) {
        if (!cancelled) {
          setMessage({
            type: 'error',
            text: err.response?.data?.error || err.response?.data?.details || 'Failed to load skills',
          });
          setSkills([]);
        }
      } finally {
        if (!cancelled) setSkillsLoading(false);
      }
    };
    loadSkills();
    return () => {
      cancelled = true;
    };
  }, [typeId]);

  useEffect(() => {
    if (!typeId || !skillSel) {
      setExamples([]);
      return;
    }
    if (skillSel === 'new') {
      setSkillLoading(false);
      setForm({ name: '', definition: '', advantage: '', level: 1 });
      setTickedKeywords(new Set());
      setExamples([]);
      return;
    }
    const sid = parseInt(skillSel, 10);
    if (Number.isNaN(sid)) return;

    let cancelled = false;
    const loadSkill = async () => {
      setSkillLoading(true);
      setMessage({ type: '', text: '' });
      try {
        const res = await api.get(`/data/writing-skill/skill/${sid}`);
        if (cancelled) return;
        const s = res.data?.skill;
        const kws = res.data?.keywords || [];
        setForm({
          name: s?.name != null ? String(s.name) : '',
          definition: s?.definition != null ? String(s.definition) : '',
          advantage: s?.advantage != null ? String(s.advantage) : '',
          level: normalizeLevel(s?.level ?? 1),
        });
        setTickedKeywords(new Set(kws.map((k) => String(k).trim()).filter(Boolean)));
        setExamples(res.data?.examples || []);
      } catch (err) {
        if (!cancelled) {
          setMessage({
            type: 'error',
            text: err.response?.data?.error || err.response?.data?.details || 'Failed to load skill',
          });
        }
      } finally {
        if (!cancelled) setSkillLoading(false);
      }
    };
    loadSkill();
    return () => {
      cancelled = true;
      setSkillLoading(false);
    };
  }, [typeId, skillSel]);

  const toggleKeyword = (value) => {
    const v = String(value).trim();
    if (!v) return;
    setTickedKeywords((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  const handleAddKeyword = (e) => {
    e.preventDefault();
    const v = newKeyword.trim();
    if (!v) return;
    setKeywordPool((prev) => {
      const set = new Set(prev.map(String));
      set.add(v);
      return [...set].sort((a, b) => String(a).localeCompare(String(b)));
    });
    setTickedKeywords((prev) => new Set(prev).add(v));
    setNewKeyword('');
  };

  const handleAutoGeneration = async () => {
    const name = form.name.trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Name is required.' });
      return;
    }
    const skillTypeValue =
      types.find((t) => String(t.skill_type_id) === typeId)?.value ?? '';

    setAutoGenLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/data/writing-skill/generate-text', {
        name,
        ...(skillTypeValue ? { skillTypeValue } : {}),
      });
      const definition = res.data?.definition != null ? String(res.data.definition) : '';
      const advantage = res.data?.advantage != null ? String(res.data.advantage) : '';
      setForm((f) => ({ ...f, definition, advantage }));
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to generate',
      });
    } finally {
      setAutoGenLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!typeId) {
      setMessage({ type: 'error', text: 'Select a skill type.' });
      return;
    }
    if (!skillSel) {
      setMessage({ type: 'error', text: 'Select a skill or create new.' });
      return;
    }
    const name = form.name.trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Name is required.' });
      return;
    }

    const skillId =
      skillSel === 'new' ? null : parseInt(skillSel, 10);
    if (skillSel !== 'new' && Number.isNaN(skillId)) {
      setMessage({ type: 'error', text: 'Invalid skill selection.' });
      return;
    }

    setSaveLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/data/writing-skill/save', {
        skillId,
        skillTypeId: parseInt(typeId, 10),
        name,
        definition: form.definition,
        advantage: form.advantage,
        level: normalizeLevel(form.level),
        keywords: [...tickedKeywords],
      });
      const savedId = res.data?.skillId;
      setMessage({ type: 'success', text: 'Saved.' });
      await loadKeywordPool();
      const skillsRes = await api.get('/data/writing-skill/skills', { params: { typeId } });
      setSkills(skillsRes.data?.skills || []);
      if (savedId != null) {
        setSkillSel(String(savedId));
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || err.response?.data?.details || 'Failed to save',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteExample = async (exampleId) => {
    const skillId = parseInt(skillSel, 10);
    if (Number.isNaN(skillId)) return;

    setDeletingExampleId(exampleId);
    setMessage({ type: '', text: '' });
    try {
      await api.delete(`/data/writing-skill/skill/${skillId}/examples/${exampleId}`);
      setExamples((prev) => prev.filter((e) => e.skill_example_id !== exampleId));
      setMessage({ type: 'success', text: 'Example unlinked from this skill.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error || err.response?.data?.details || 'Failed to delete example',
      });
    } finally {
      setDeletingExampleId(null);
    }
  };

  const formReady = Boolean(typeId && skillSel);
  const showExamples =
    Boolean(typeId && skillSel && skillSel !== 'new');
  const showForm = formReady && !skillLoading;

  return (
    <div className="dashboard">
      <DashboardHeader title="Writing Skill" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 900, margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <h2>Skill</h2>

            <div className="form-group">
              <label htmlFor="ws-skill-type">
                Skill type <span className="required">*</span>
              </label>
              {typesLoading ? (
                <div className="loading-state">Loading…</div>
              ) : (
                <select
                  id="ws-skill-type"
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                >
                  <option value="">— Select type —</option>
                  {types.map((t) => (
                    <option key={t.skill_type_id} value={String(t.skill_type_id)}>
                      {t.value}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {typeId && (
              <div className="form-group">
                <label htmlFor="ws-skill-name-select">
                  Skill <span className="required">*</span>
                </label>
                {skillsLoading ? (
                  <div className="loading-state">Loading skills…</div>
                ) : (
                  <select
                    id="ws-skill-name-select"
                    value={skillSel}
                    onChange={(e) => setSkillSel(e.target.value)}
                  >
                    <option value="">— Select skill —</option>
                    <option value="new">＋ Create new</option>
                    {skills.map((s) => (
                      <option key={s.skill_id} value={String(s.skill_id)}>
                        {s.name || `(id ${s.skill_id})`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {skillSel && skillLoading && <div className="loading-state">Loading skill…</div>}

            {showForm && (
              <form onSubmit={handleSave}>
                <div className="form-group">
                  <label htmlFor="ws-name">
                    name <span className="required">*</span>
                  </label>
                  <textarea
                    id="ws-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={saveLoading || autoGenLoading}
                    rows={2}
                    style={{
                      ...textInputStyle,
                      minHeight: MIN_TEXTAREA_HEIGHT_PX,
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleAutoGeneration}
                    disabled={saveLoading || autoGenLoading}
                  >
                    {autoGenLoading ? 'Generating…' : 'Auto generation'}
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="ws-definition">definition</label>
                  <textarea
                    id="ws-definition"
                    value={form.definition}
                    onChange={(e) => setForm((f) => ({ ...f, definition: e.target.value }))}
                    disabled={saveLoading || autoGenLoading}
                    rows={4}
                    style={{
                      ...textInputStyle,
                      minHeight: MIN_TEXTAREA_HEIGHT_PX,
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ws-advantage">advantage</label>
                  <textarea
                    id="ws-advantage"
                    value={form.advantage}
                    onChange={(e) => setForm((f) => ({ ...f, advantage: e.target.value }))}
                    disabled={saveLoading || autoGenLoading}
                    rows={4}
                    style={{
                      ...textInputStyle,
                      minHeight: MIN_TEXTAREA_HEIGHT_PX,
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ws-level">level</label>
                  <input
                    id="ws-level"
                    type="number"
                    min={1}
                    value={form.level}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, level: normalizeLevel(e.target.value) }))
                    }
                    disabled={saveLoading || autoGenLoading}
                    style={{ ...textInputStyle, maxWidth: 120 }}
                  />
                </div>

                <div className="form-group">
                  <span style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                    Keywords
                  </span>
                  <p className="form-hint" style={{ marginTop: 0, marginBottom: 12 }}>
                    Tick values from the pool; use create new to add a keyword (saved on Save).
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '10px 16px',
                      marginBottom: 16,
                      alignItems: 'center',
                      width: '100%',
                      maxWidth: '100%',
                    }}
                  >
                    {keywordPool.map((kw) => {
                      const ks = String(kw).trim();
                      return (
                        <label
                          key={ks}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                            flex: '0 0 auto',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={tickedKeywords.has(ks)}
                            onChange={() => toggleKeyword(ks)}
                            disabled={saveLoading || autoGenLoading}
                          />
                          <span>{ks}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="New keyword"
                      disabled={saveLoading || autoGenLoading}
                      style={{ ...textInputStyle, flex: '1 1 200px', maxWidth: 400 }}
                    />
                    <button
                      type="button"
                      className="submit-button"
                      onClick={handleAddKeyword}
                      disabled={saveLoading || autoGenLoading}
                    >
                      Add keyword
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 20 }}>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={saveLoading || autoGenLoading}
                  >
                    {saveLoading ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {showExamples && (
            <div className="form-section" style={{ marginTop: 24 }}>
              <h2>Example</h2>
              {skillLoading ? (
                <div className="loading-state">Loading examples…</div>
              ) : examples.length === 0 ? (
                <p className="form-hint" style={{ marginTop: 0 }}>
                  No examples for this skill.
                </p>
              ) : (
                <div className="generated-questions-list">
                  {examples.map((ex) => (
                    <div key={ex.skill_example_id} className="generated-question-item">
                      <div className="question-item-header">
                        <p
                          style={{
                            ...textInputStyle,
                            margin: 0,
                            flex: 1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {ex.value}
                        </p>
                        <button
                          type="button"
                          className="delete-question-button"
                          onClick={() => handleDeleteExample(ex.skill_example_id)}
                          disabled={
                            deletingExampleId === ex.skill_example_id ||
                            saveLoading ||
                            autoGenLoading
                          }
                        >
                          {deletingExampleId === ex.skill_example_id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WritingSkill;
